import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CameraIcon, ChevronDown, HelpCircle, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DeleteIcon, UsersIcon, PlusIcon, ResetIcon } from '@/components/Icons'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useEffect, useState } from 'react'
import { FileUpload } from '../ui/file-upload'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'
import { showErrorToast } from '../ui/toast'

interface EventAddUserFormProps {
  handleImageSelect: (
    files: File | File[] | null,
    type: 'eventImage' | 'guestImage',
    id?: string
  ) => Promise<void>
}

export default function EventAddUserForm({
  handleImageSelect,
}: Readonly<EventAddUserFormProps>) {
  const { control, watch, formState, setValue, clearErrors } =
    useFormContext<EventFormDataType>()
  const users = watch('users') || []
  const [isAddUsersOpen, setIsAddUsersOpen] = useState(false)

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
  })

  useEffect(() => {
    if (users.length > 1) {
      setIsAddUsersOpen(true)
    } else if (users.length === 1) {
      const userData = users[0]
      if (
        !(
          userData.id === '' &&
          userData.fullName === '' &&
          userData.designation === '' &&
          userData.isNew === true
        )
      ) {
        setIsAddUsersOpen(true)
      }
    }
  }, [])

  const clearUserErrors = (index: number) => {
    clearErrors([
      `users.${index}.fullName`,
      `users.${index}.designation`,
      `users.${index}.avatarFile`,
    ])
  }

  const handleAddUser = () => {
    if (fields.length >= 5) return
    append({
      id: Date.now().toString(),
      fullName: '',
      designation: '',
      isNew: true,
    })
  }

  const handleRemoveUser = (index: number) => {
    clearUserErrors(index)
    remove(index)
  }

  const getUserErrors = (index: number) => {
    const errors = formState.errors.users?.[index]
    if (!errors) return []
    const userErrors: string[] = []
    if (errors.fullName) {
      userErrors.push(errors.fullName.message || 'Full name is required')
    }
    if (errors.designation) {
      userErrors.push(errors.designation.message || 'Designation is required')
    }
    if (errors.avatarFile) {
      userErrors.push(errors.avatarFile.message || 'Profile image is required')
    }
    if (errors.message) {
      userErrors.push(errors.message)
    }
    if (errors.root?.message) {
      userErrors.push(errors.root.message)
    }

    return userErrors
  }

  const handleFileUploadError = (error: string) => {
    showErrorToast(error)
  }

  const handleUserReset = (index: number) => {
    setValue(`users.${index}.fullName`, '')
    setValue(`users.${index}.designation`, '')
    setValue(`users.${index}.avatarFile`, undefined)
    clearUserErrors(index)
  }

  return (
    <>
      {/* Add Users Section */}
      <div>
        <CardHeader
          className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3 cursor-pointer hover:bg-ss-neutral-50"
          onClick={() => setIsAddUsersOpen(!isAddUsersOpen)}
        >
          <UsersIcon className="h-5 w-5 text-ss-neutral-600" />
          <CardTitle className="text-lg font-semibold text-ss-neutral-950 flex items-center gap-1.5">
            Add to Spotlight
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-4 w-4 text-ss-neutral-600"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>You can add up-to 5 users</TooltipContent>
            </Tooltip>
          </CardTitle>
          <div className="ml-auto">
            <ChevronDown
              className={cn(
                'h-5 w-5 text-ss-neutral-600 transition-transform',
                isAddUsersOpen && 'rotate-180'
              )}
            />
          </div>
        </CardHeader>
        {isAddUsersOpen && (
          <CardContent className="p-4 space-y-4">
            {/* Users List */}
            {fields.map((field, index) => {
              const user = watch(`users.${index}`)
              const userErrors = getUserErrors(index)
              return (
                <div key={field.id}>
                  <div className="flex gap-4 sm:items-end items-center flex-col sm:flex-row mb-0 sm:mb-4">
                    {/* Avatar */}
                    <div className="flex flex-row justify-between w-full sm:w-auto align-middle gap-2 sm:items-start">
                      <FileUpload
                        onError={handleFileUploadError}
                        onFileSelect={file =>
                          handleImageSelect(file, 'guestImage', user?.id)
                        }
                        getDropZone={dropzoneState => {
                          return (
                            <div className="relative">
                              <div
                                {...dropzoneState.getRootProps()}
                                className="relative cursor-pointer group"
                              >
                                <input {...dropzoneState.getInputProps()} />
                                <Avatar
                                  className="h-16 w-16 rounded-full border border-ss-neutral-100 transition-all group-hover:border-ss-blue-300"
                                  style={{
                                    minWidth: '64px',
                                    minHeight: '64px',
                                  }}
                                >
                                  {user?.avatarFile ? (
                                    <img
                                      src={URL.createObjectURL(user.avatarFile)}
                                      alt={user.fullName || 'User avatar'}
                                      className="object-cover w-16 h-16 rounded-full"
                                    />
                                  ) : (
                                    <AvatarFallback className="bg-ss-blue-100 w-16 h-16 rounded-full flex items-center justify-center">
                                      <User className="w-8 h-8 text-ss-neutral-300" />
                                    </AvatarFallback>
                                  )}
                                </Avatar>

                                <div className="absolute bottom-0 right-0 w-5 h-5 flex items-center justify-center text-white bg-ss-blue-500 rounded-full border-2 border-white shadow-sm">
                                  <CameraIcon className="w-3 h-3 " />
                                </div>
                              </div>
                            </div>
                          )
                        }}
                      />
                      <div className="pb-1.5 flex">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => handleRemoveUser(index)}
                          className="size-6 p-0 text-ss-neutral-950 stroke-1 hover:text-ss-critical-200 hover:bg-ss-critical-50  sm:hidden"
                        >
                          <DeleteIcon className="size-6" />
                        </Button>
                      </div>
                    </div>

                    {/* Full Name Input */}
                    <div className="flex-1 space-y-1 w-full">
                      <Label
                        variant="required"
                        className="text-sm font-semibold text-ss-neutral-950"
                      >
                        Full Name
                      </Label>
                      <Controller
                        name={`users.${index}.fullName`}
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              {...field}
                              required
                              type="text"
                              placeholder="Enter full name"
                              className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                            />
                          </>
                        )}
                      />
                    </div>

                    {/* Designation Input */}
                    <div className="flex-1 space-y-1 w-full">
                      <Label
                        variant="required"
                        className="text-sm font-semibold text-ss-neutral-950"
                      >
                        Designation
                      </Label>
                      <Controller
                        name={`users.${index}.designation`}
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              {...field}
                              required
                              type="text"
                              placeholder="Enter designation"
                              className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                            />
                          </>
                        )}
                      />
                    </div>

                    {/* Delete Button */}
                    {fields.length > 1 && (
                      <div className="pb-1.5 flex">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => handleRemoveUser(index)}
                          className="size-6 p-0 text-ss-neutral-950 stroke-1 hover:text-ss-critical-200 hover:bg-ss-critical-50 hidden sm:flex"
                        >
                          <DeleteIcon className="size-6" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {userErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
                      <ul className="text-sm text-red-700 space-y-1">
                        {userErrors.map((error, errorIndex) => (
                          <li
                            key={errorIndex}
                            className="flex items-start gap-1"
                          >
                            <span className="text-red-500 mt-0.5">•</span>
                            <span>{error}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })}
            <div className="w-full flex justify-end">
              {fields.length === 1 && (
                <Button
                  disabled={
                    fields.length >= 5 ||
                    (!users[0]?.fullName &&
                      !users[0]?.designation &&
                      !users[0]?.avatarFile)
                  }
                  type="button"
                  variant="ghost"
                  aria-label="Reset user fields"
                  className="p-0 text-ss-blue-900 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-900 hover:bg-transparent"
                  onClick={() => {
                    if (fields.length === 1) {
                      handleUserReset(0)
                    }
                  }}
                >
                  <ResetIcon
                    className="size-5 hover:text-ss-blue-900"
                    aria-hidden="true"
                  />
                  <span className="font-medium  text-sm leading-4.5   hover:text-ss-blue-900 disabled:text-ss-neutral-200">
                    Reset
                  </span>
                </Button>
              )}

              <Button
                disabled={fields.length >= 5}
                type="button"
                variant="ghost"
                onClick={handleAddUser}
                aria-label="Add more users"
                className="p-0 text-ss-blue-600 stroke-1 gap-1 h-auto justify-end disabled:bg-transparent disabled:text-ss-neutral-300 hover:text-ss-blue-700 hover:bg-transparent"
              >
                <PlusIcon
                  className="size-5 hover:text-ss-blue-700"
                  aria-hidden="true"
                />
                <span className="font-medium text-sm leading-4.5  hover:text-ss-blue-700 disabled:text-ss-neutral-200">
                  Add More
                </span>
              </Button>
            </div>
          </CardContent>
        )}
      </div>
    </>
  )
}
